import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import { useAppContext } from "../../context";
import { buttonVariants } from "./button";
import { cn } from "../../lib/utils";
import { ExternalLink, X } from "lucide-react";

interface ReferralBannerData {
  enabled: boolean;
  url?: string;
  title?: string;
  description?: string;
  buttonText?: string;
}

const DISMISSED_KEY = "referral-banner-dismissed";

export function ReferralBanner() {
  const { selectedMachine } = useAppContext();
  const [isDismissed, setIsDismissed] = useState(() => {
    // Check if banner was dismissed in this session
    return sessionStorage.getItem(DISMISSED_KEY) === "true";
  });

  const { data: bannerData } = useQuery({
    queryKey: ["referral-banner"],
    queryFn: async () => {
      const response = await api.settings.referral.get();
      if (response.data && "enabled" in response.data) {
        return response.data as ReferralBannerData;
      }
      return { enabled: false };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Don't show if dismissed, not enabled, or no URL
  if (isDismissed || !bannerData?.enabled || !bannerData?.url) {
    return null;
  }

  const handleClick = () => {
    // Track the click (fire-and-forget)
    api.settings.referral.click.post({
      machineCode: selectedMachine ?? undefined,
    }).catch(() => {
      // Ignore tracking errors
    });
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem(DISMISSED_KEY, "true");
  };

  return (
    <div className="relative bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg p-4 mb-6">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 hover:bg-amber-500/20 rounded-full transition-colors"
        aria-label="Dismiss banner"
      >
        <X className="w-4 h-4 text-amber-400/60" />
      </button>
      <div className="flex items-center justify-between gap-4 pr-6">
        <div>
          <h3 className="font-semibold text-amber-400">
            {bannerData.title || "Play on CollectorCrypt"}
          </h3>
          <p className="text-sm text-amber-400/80 mt-1">
            {bannerData.description ||
              "Use our referral link to play the gacha machines!"}
          </p>
        </div>
        <a
          href={bannerData.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className={cn(
            buttonVariants({ size: "sm" }),
            "bg-amber-500 hover:bg-amber-600 text-black shrink-0"
          )}
        >
          {bannerData.buttonText || "Play Now"}
          <ExternalLink className="w-4 h-4 ml-2" />
        </a>
      </div>
    </div>
  );
}
